import { useState, useEffect, useRef } from 'react';
import {
  LineChart, Line, BarChart, Bar, ComposedChart,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  ReferenceLine, Cell,
} from 'recharts';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const fmt = v => {
  if (!v && v !== 0) return '—';
  const n = Number(v);
  if (n >= 10000000) return `₹${(n/10000000).toFixed(2)}Cr`;
  if (n >= 100000)   return `₹${(n/100000).toFixed(2)}L`;
  if (n >= 1000)     return `₹${(n/1000).toFixed(1)}K`;
  return `₹${n.toLocaleString('en-IN')}`;
};
const fmtN  = v => Number(v||0).toLocaleString('en-IN');
const pctLbl = v => v != null ? `${v > 0 ? '+' : ''}${v}%` : '—';

const BRAND  = '#403EC9';
const GREEN  = '#16A34A';
const AMBER  = '#D97706';
const RED    = '#DC2626';
const PURPLE = '#7C3AED';

const achieveColor = pct => pct >= 80 ? GREEN : pct >= 50 ? AMBER : RED;

function MetricCard({ label, value, sub, color = BRAND, badge, right }) {
  return (
    <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:'18px 20px', display:'flex', flexDirection:'column', gap:3 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div style={{ fontSize:'0.7rem', fontWeight:600, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.07em' }}>{label}</div>
        {right && <div style={{ fontSize:'0.7rem', color:'#9CA3AF' }}>{right}</div>}
      </div>
      <div style={{ fontSize:'1.55rem', fontWeight:800, color, lineHeight:1.2, marginTop:2 }}>{value}</div>
      {sub && <div style={{ fontSize:'0.75rem', color:'#9CA3AF', marginTop:1 }}>{sub}</div>}
      {badge && (
        <div style={{ marginTop:6, display:'inline-flex', alignItems:'center', gap:4, background:badge.bg, color:badge.fg, borderRadius:20, padding:'2px 9px', fontSize:'0.7rem', fontWeight:600, width:'fit-content' }}>
          {badge.icon} {badge.label}
        </div>
      )}
    </div>
  );
}

function SectionHead({ title, sub }) {
  return (
    <div style={{ marginTop:36, marginBottom:14, borderLeft:`3px solid ${BRAND}`, paddingLeft:12 }}>
      <div style={{ fontSize:'1rem', fontWeight:800, color:'#111827' }}>{title}</div>
      {sub && <div style={{ fontSize:'0.76rem', color:'#6B7280', marginTop:2 }}>{sub}</div>}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, padding:'10px 14px', fontSize:'0.78rem', boxShadow:'0 4px 12px rgba(0,0,0,0.08)' }}>
      <div style={{ fontWeight:700, marginBottom:4, color:'#111827' }}>{label}</div>
      {payload.map(p => (
        <div key={p.name} style={{ color: p.color, display:'flex', gap:8 }}>
          <span style={{ color:'#6B7280' }}>{p.name}:</span>
          <span style={{ fontWeight:600 }}>{typeof p.value === 'number' && p.value > 1000 ? fmt(p.value) : fmtN(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

export default function InvestorDashboard() {
  const { user, logout } = useAuth();
  const [data, setData]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [skuSort, setSkuSort] = useState('target');
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [pwdForm, setPwdForm]           = useState({ current:'', next:'', confirm:'' });
  const [pwdMsg, setPwdMsg]             = useState(null);
  const [pwdLoading, setPwdLoading]     = useState(false);

  // Feature-flagged section state
  const [portalConfig, setPortalConfig]         = useState({});
  const [updates, setUpdates]                   = useState([]);
  const [perfNotes, setPerfNotes]               = useState([]);
  const [exitRoadmap, setExitRoadmap]           = useState(null);
  const [capTableData, setCapTableData]         = useState(null);
  const [boardResolutions, setBoardResolutions] = useState([]);
  const [liqModal, setLiqModal]                 = useState(false);
  const [liqForm, setLiqForm]                   = useState({ reason:'', amount_requested:'', shares_to_liquidate:'' });
  const [liqSubmitting, setLiqSubmitting]       = useState(false);
  const [liqMsg, setLiqMsg]                     = useState(null);
  const [expandedUpdate, setExpandedUpdate]     = useState(null);
  const [expandedNote, setExpandedNote]         = useState(null);

  const handleChangePwd = async e => {
    e.preventDefault();
    if (pwdForm.next !== pwdForm.confirm) { setPwdMsg({ ok:false, text:'New passwords do not match.' }); return; }
    if (pwdForm.next.length < 8)          { setPwdMsg({ ok:false, text:'Password must be at least 8 characters.' }); return; }
    setPwdLoading(true); setPwdMsg(null);
    try {
      await api.put('/auth/password', { currentPassword: pwdForm.current, newPassword: pwdForm.next });
      setPwdMsg({ ok:true, text:'Password updated successfully.' });
      setPwdForm({ current:'', next:'', confirm:'' });
      setTimeout(() => setShowPwdModal(false), 1500);
    } catch(err) {
      setPwdMsg({ ok:false, text: err?.response?.data?.message || 'Failed to update password.' });
    } finally { setPwdLoading(false); }
  };

  const markRead = async (updateId) => {
    try { await api.post(`/investor-portal/updates/${updateId}/read`); } catch {}
    setUpdates(prev => prev.map(u => u.id === updateId ? { ...u, is_read: true } : u));
  };

  const [error, setError] = useState(null);
  useEffect(() => {
    // Load metrics + config in parallel, then conditionally fetch gated data
    Promise.all([
      api.get('/dashboard/investor'),
      api.get('/investor-portal/config').catch(() => ({ data: { data: {} } })),
    ]).then(async ([metricsR, configR]) => {
      setData(metricsR.data.data);
      const cfg = configR.data.data || {};
      setPortalConfig(cfg);
      // Only fetch sections that are enabled
      const fetches = [];
      if (cfg.investor_updates_enabled)
        fetches.push(api.get('/investor-portal/updates').then(r => setUpdates(r.data.data || [])).catch(() => {}));
      if (cfg.investor_performance_notes_enabled)
        fetches.push(api.get('/investor-portal/performance-notes').then(r => setPerfNotes(r.data.data || [])).catch(() => {}));
      if (cfg.investor_exit_roadmap_enabled)
        fetches.push(api.get('/investor-portal/exit-roadmap').then(r => setExitRoadmap(r.data.data || null)).catch(() => {}));
      if (cfg.investor_cap_table_enabled)
        fetches.push(api.get('/investor-portal/cap-table').then(r => setCapTableData(r.data.data || null)).catch(() => {}));
      if (cfg.investor_board_resolutions_enabled)
        fetches.push(api.get('/investor-portal/board-resolutions').then(r => setBoardResolutions(r.data.data || [])).catch(() => {}));
      await Promise.all(fetches);
    })
    .catch(e => setError(e?.response?.data?.message || 'Failed to load metrics'))
    .finally(() => setLoading(false));
  }, []);

  if (error) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#F3F4F6' }}>
      <div style={{ textAlign:'center', color:'#DC2626' }}>
        <div style={{ fontSize:'2rem', marginBottom:8 }}>⚠️</div>
        <div style={{ fontWeight:600 }}>{error}</div>
        <button onClick={() => window.location.reload()} style={{ marginTop:16, padding:'8px 20px', background:'#403EC9', color:'#fff', border:'none', borderRadius:6, cursor:'pointer' }}>Retry</button>
      </div>
    </div>
  );

  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#F3F4F6' }}>
      <div style={{ textAlign:'center', color:'#6B7280' }}>
        <div style={{ fontSize:'2.5rem', marginBottom:8 }}>📊</div>
        <div style={{ fontWeight:600 }}>Loading investor metrics…</div>
      </div>
    </div>
  );

  const { revenue, projection, quarters, monthly_trend, doctors, territories, pipeline, sku_performance, field_activity, doctor_growth, deals } = data;

  const yoyBadge = revenue.yoy_growth != null
    ? { bg: revenue.yoy_growth >= 0 ? '#D1FAE5' : '#FEE2E2', fg: revenue.yoy_growth >= 0 ? '#065F46' : '#991B1B', icon: revenue.yoy_growth >= 0 ? '▲' : '▼', label: `${Math.abs(revenue.yoy_growth)}% YoY` }
    : null;

  const projBadge = { bg: projection.on_track ? '#D1FAE5' : '#FEF3C7', fg: projection.on_track ? '#065F46' : '#92400E', icon: projection.on_track ? '✓' : '⚠', label: projection.on_track ? 'On track' : `${fmt(Math.abs(projection.projection_gap))} gap` };

  // Monthly trend with target line
  const trendData = monthly_trend.map(m => ({ ...m, target: revenue.monthly_target }));

  // SKU table sorted
  const sortedSku = [...(sku_performance||[])].sort((a,b) => {
    if (skuSort === 'target')   return b.annual_target - a.annual_target;
    if (skuSort === 'actual')   return b.ytd_actual - a.actual;
    if (skuSort === 'achieve')  return b.achievement_pct - a.achievement_pct;
    if (skuSort === 'growth')   return (b.yoy_growth||0) - (a.yoy_growth||0);
    return 0;
  });

  const pipelineStages = [
    { label:'Approved',    key:'approved',    color:'#60A5FA' },
    { label:'Invoiced',    key:'invoiced',    color:'#818CF8' },
    { label:'Dispatched',  key:'dispatched',  color:'#A78BFA' },
    { label:'Delivered',   key:'delivered',   color:'#34D399' },
    { label:'Collected',   key:'settled',     color: GREEN    },
  ];
  const totalPipelineValue = pipeline.active_value + pipeline.collected_value;

  return (
    <div style={{ minHeight:'100vh', background:'#F3F4F6', fontFamily:'Inter,system-ui,sans-serif' }}>

      {/* Nav bar */}
      <div style={{ background:'#fff', borderBottom:'1px solid #E5E7EB', padding:'0 32px', display:'flex', alignItems:'center', justifyContent:'space-between', height:56, position:'sticky', top:0, zIndex:100 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:30, height:30, background:BRAND, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <span style={{ color:'#fff', fontSize:'0.75rem', fontWeight:800 }}>A</span>
          </div>
          <span style={{ fontWeight:800, fontSize:'1rem', color:'#111827' }}>alveofit</span>
          <span style={{ background:'#EEF2FF', color:BRAND, borderRadius:20, padding:'2px 10px', fontSize:'0.65rem', fontWeight:700, marginLeft:4, letterSpacing:'0.05em' }}>INVESTOR VIEW</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:16 }}>
          <span style={{ fontSize:'0.75rem', color:'#9CA3AF' }}>Live data · {new Date().toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}</span>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <div style={{ width:30, height:30, borderRadius:'50%', background:BRAND, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:'0.75rem', fontWeight:700 }}>
              {user?.name?.[0] || 'I'}
            </div>
            <span style={{ fontSize:'0.8rem', color:'#374151', fontWeight:500 }}>{user?.name || 'Investor'}</span>
          </div>
          <button onClick={() => { setShowPwdModal(true); setPwdMsg(null); }} style={{ border:'1px solid #E5E7EB', background:'#fff', color:'#6B7280', borderRadius:6, padding:'5px 14px', fontSize:'0.75rem', cursor:'pointer' }}>🔑 Change Password</button>
          <button onClick={logout} style={{ border:'1px solid #E5E7EB', background:'#fff', color:'#6B7280', borderRadius:6, padding:'5px 14px', fontSize:'0.75rem', cursor:'pointer' }}>Sign out</button>
        </div>
      </div>

      <div style={{ maxWidth:1280, margin:'0 auto', padding:'28px 24px 80px' }}>

        {/* Title */}
        <div style={{ marginBottom:24 }}>
          <h1 style={{ margin:0, fontSize:'1.5rem', fontWeight:800, color:'#111827' }}>Business Overview — FY 2026–27</h1>
          <p style={{ margin:'4px 0 0', color:'#6B7280', fontSize:'0.82rem' }}>Alveofit · Roundworks Technologies Pvt Ltd · Confidential</p>
        </div>

        {/* ── UNIFIED: Revenue Reality & Road Ahead ── */}
        {(() => {
          const ytd       = revenue.ytd_actual || 0;
          const target    = revenue.annual_target || 1;
          const pipelineV = deals?.pipeline_value || 0;
          const gap       = Math.max(0, target - ytd);
          const q2Base    = Math.round(pipelineV * 0.25);
          const q2Cons    = Math.round(pipelineV * 0.15);
          const q2Opt     = Math.round(pipelineV * 0.40);

          // Progress bar widths (capped at 100% of track)
          const ytdPct      = Math.min(Math.round(ytd / target * 100), 100);
          const pipePct     = Math.min(Math.round(pipelineV / target * 100), 100 - ytdPct);
          const remainPct   = Math.max(0, 100 - ytdPct - pipePct);

          const stageColors = [BRAND, PURPLE, AMBER];
          const stageIcons  = ['🔍', '🧪', '🤝'];
          const maxCount    = deals ? Math.max(...deals.stages.map(s => s.count), 1) : 1;

          return (
            <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:14, padding:'24px 28px', marginBottom:20 }}>

              {/* Section header */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
                <div>
                  <div style={{ fontWeight:800, fontSize:'1.05rem', color:'#111827' }}>Revenue Reality & Road Ahead</div>
                  <div style={{ fontSize:'0.78rem', color:'#9CA3AF', marginTop:3 }}>Live actuals + active deal pipeline · Path to FY26–27 target</div>
                </div>
                {yoyBadge && (
                  <div style={{ display:'inline-flex', alignItems:'center', gap:4, background: yoyBadge.bg, color: yoyBadge.fg, borderRadius:20, padding:'3px 10px', fontSize:'0.72rem', fontWeight:700 }}>
                    {yoyBadge.icon} {yoyBadge.label}
                  </div>
                )}
              </div>

              {/* Top 4 headline metrics */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:28 }}>
                {[
                  { label:'YTD Closed',      value: fmt(ytd),        sub: `${revenue.achievement_pct}% of annual target`, color: achieveColor(revenue.achievement_pct) },
                  { label:'Gap to Target',   value: fmt(gap),        sub: `${fmt(target)} annual · ${projection.months_remaining}mo left`, color: gap > 0 ? AMBER : GREEN },
                  { label:'Active Pipeline', value: fmt(pipelineV),  sub: `${deals?.open_count || 0} live deals being pursued`, color: BRAND },
                  { label:'Q2 Projection',   value: fmt(q2Base),     sub: 'Base case · 25% pipeline close rate', color: PURPLE },
                ].map(k => (
                  <div key={k.label} style={{ background:'#F9FAFB', borderRadius:10, padding:'14px 16px', borderLeft:`3px solid ${k.color}` }}>
                    <div style={{ fontSize:'0.66rem', color:'#9CA3AF', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>{k.label}</div>
                    <div style={{ fontSize:'1.45rem', fontWeight:800, color: k.color, lineHeight:1.1 }}>{k.value}</div>
                    <div style={{ fontSize:'0.71rem', color:'#6B7280', marginTop:4 }}>{k.sub}</div>
                  </div>
                ))}
              </div>

              {/* PATH TO TARGET — stacked progress bar */}
              <div style={{ marginBottom:24 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <span style={{ fontSize:'0.7rem', fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.06em' }}>Path to ₹{(target/10000000).toFixed(2)}Cr Annual Target</span>
                  <span style={{ fontSize:'0.7rem', color:'#9CA3AF' }}>{ytdPct + pipePct}% covered (actuals + pipeline)</span>
                </div>
                <div style={{ height:32, background:'#F3F4F6', borderRadius:8, overflow:'hidden', display:'flex' }}>
                  {/* YTD actual */}
                  <div style={{ width:`${ytdPct}%`, height:'100%', background: achieveColor(revenue.achievement_pct), display:'flex', alignItems:'center', justifyContent:'center', transition:'width 0.8s ease', minWidth: ytdPct > 0 ? 2 : 0 }}>
                    {ytdPct > 8 && <span style={{ fontSize:'0.68rem', fontWeight:700, color:'#fff', whiteSpace:'nowrap', padding:'0 6px' }}>YTD {ytdPct}%</span>}
                  </div>
                  {/* Pipeline (hatched look via opacity) */}
                  <div style={{ width:`${pipePct}%`, height:'100%', background: BRAND, opacity:0.35, display:'flex', alignItems:'center', justifyContent:'center', transition:'width 0.8s ease', minWidth: pipePct > 0 ? 2 : 0 }}>
                    {pipePct > 8 && <span style={{ fontSize:'0.68rem', fontWeight:700, color: BRAND, opacity:3, whiteSpace:'nowrap', padding:'0 6px' }}>Pipeline {pipePct}%</span>}
                  </div>
                  {/* Remaining gap */}
                  <div style={{ flex:1, height:'100%', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {remainPct > 5 && <span style={{ fontSize:'0.66rem', color:'#9CA3AF', whiteSpace:'nowrap' }}>Gap {remainPct}%</span>}
                  </div>
                </div>
                {/* Legend */}
                <div style={{ display:'flex', gap:16, marginTop:8 }}>
                  {[
                    { color: achieveColor(revenue.achievement_pct), label:`Closed · ${fmt(ytd)}` },
                    { color: BRAND, opacity:0.35, label:`Pipeline · ${fmt(pipelineV)}` },
                    { color:'#E5E7EB', label:`Remaining gap · ${fmt(gap - pipelineV > 0 ? gap - pipelineV : 0)}` },
                  ].map(l => (
                    <div key={l.label} style={{ display:'flex', alignItems:'center', gap:5, fontSize:'0.68rem', color:'#6B7280' }}>
                      <div style={{ width:10, height:10, borderRadius:2, background: l.color, opacity: l.opacity || 1 }} />
                      {l.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Bottom row: deal stages LEFT + Q2 scenarios RIGHT */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24, paddingTop:20, borderTop:'1px solid #F3F4F6' }}>

                {/* Deal stage funnel */}
                <div>
                  <div style={{ fontSize:'0.7rem', fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:12 }}>Deal Stage Progression</div>
                  {deals && deals.stages.map((s, i) => {
                    const pct = Math.round(s.count / maxCount * 100);
                    return (
                      <div key={s.label} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                        <div style={{ width:90, fontSize:'0.75rem', color:'#374151', fontWeight:600, flexShrink:0 }}>{stageIcons[i]} {s.label}</div>
                        <div style={{ flex:1, height:24, background:'#F3F4F6', borderRadius:5, overflow:'hidden' }}>
                          <div style={{ width:`${pct}%`, height:'100%', background: stageColors[i], borderRadius:5, minWidth: s.count > 0 ? 4 : 0, transition:'width 0.6s ease' }} />
                        </div>
                        <div style={{ width:140, fontSize:'0.7rem', fontWeight:700, color: stageColors[i], flexShrink:0, whiteSpace:'nowrap' }}>
                          {s.count > 0 ? `${s.count} deals · ${fmt(s.value)}` : '—'}
                        </div>
                      </div>
                    );
                  })}
                  {deals && (
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:8, paddingTop:10, borderTop:'1px dashed #E5E7EB' }}>
                      <div style={{ width:90, fontSize:'0.75rem', color:GREEN, fontWeight:700, flexShrink:0 }}>✓ Won MTD</div>
                      <div style={{ flex:1, height:24, background:'#D1FAE5', borderRadius:5, display:'flex', alignItems:'center', paddingLeft:10 }}>
                        <span style={{ fontSize:'0.7rem', fontWeight:700, color:'#065F46', whiteSpace:'nowrap' }}>
                          {deals.won_mtd_count} closed · {fmt(deals.won_mtd_value)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Q2 conversion scenarios */}
                <div>
                  <div style={{ fontSize:'0.7rem', fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:12 }}>Q2 Revenue Scenarios from Pipeline</div>
                  {[
                    { label:'Conservative', rate:15, value: q2Cons, color:'#D97706', bg:'#FEF3C7', bar:38 },
                    { label:'Base Case',    rate:25, value: q2Base, color: BRAND,    bg:'#EEF2FF', bar:63 },
                    { label:'Optimistic',   rate:40, value: q2Opt,  color: GREEN,    bg:'#D1FAE5', bar:100 },
                  ].map(sc => (
                    <div key={sc.label} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
                      <div style={{ width:90, fontSize:'0.75rem', fontWeight:600, color:'#374151', flexShrink:0 }}>{sc.label}</div>
                      <div style={{ flex:1, height:24, background:'#F3F4F6', borderRadius:5, overflow:'hidden' }}>
                        <div style={{ width:`${sc.bar}%`, height:'100%', background: sc.color, opacity:0.8, borderRadius:5, transition:'width 0.6s ease' }} />
                      </div>
                      <div style={{ width:80, textAlign:'right', fontSize:'0.72rem', fontWeight:800, color: sc.color, flexShrink:0, whiteSpace:'nowrap' }}>{fmt(sc.value)}</div>
                      <div style={{ width:34, textAlign:'right', fontSize:'0.68rem', color:'#9CA3AF', flexShrink:0 }}>{sc.rate}%</div>
                    </div>
                  ))}
                  <div style={{ marginTop:12, padding:'10px 12px', background:'#F9FAFB', borderRadius:8, fontSize:'0.72rem', color:'#6B7280', lineHeight:1.6 }}>
                    Base case converts {deals?.stages.find(s=>s.label==='Evaluation')?.count||0} Evaluation deals at 25%
                    → <strong style={{ color:BRAND }}>{fmt(q2Base)}</strong> Q2 revenue
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── SECTION 2: Growth Plan — Run Rate vs Target ── */}
        <SectionHead title="Growth Plan — Run Rate vs Annual Target" sub="Monthly actuals (bars) against monthly target (line) and projected year-end trajectory" />
        <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:'20px 16px 12px' }}>
          {trendData.length === 0 ? (
            <div style={{ textAlign:'center', color:'#9CA3AF', padding:40 }}>No revenue data yet for this period</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={trendData} margin={{ left:10, right:24, top:4 }}>
                <XAxis dataKey="month" tick={{ fontSize:11, fill:'#9CA3AF' }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={v => fmt(v)} tick={{ fontSize:10, fill:'#9CA3AF' }} axisLine={false} tickLine={false} width={64} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize:'0.75rem', paddingTop:8 }} />
                <Bar dataKey="actual_revenue" name="Actual Revenue" fill={BRAND} radius={[4,4,0,0]} opacity={0.85} />
                <Line type="monotone" dataKey="target" name="Monthly Target" stroke={AMBER} strokeWidth={2} strokeDasharray="5 4" dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
          {/* Run-rate callout */}
          <div style={{ marginTop:12, display:'flex', gap:12, flexWrap:'wrap' }}>
            {[
              { label:'Monthly Run Rate', value: fmt(projection.run_rate_monthly), color: BRAND },
              { label:'Projected FY End', value: fmt(projection.projected_fy),    color: projection.on_track ? GREEN : AMBER },
              { label:'Gap to Target',    value: fmt(Math.abs(projection.projection_gap)), color: projection.projection_gap >= 0 ? GREEN : RED, prefix: projection.projection_gap >= 0 ? '+' : '-' },
              { label:'Months Remaining', value: `${projection.months_remaining}`, color:'#6B7280' },
            ].map(m => (
              <div key={m.label} style={{ background:'#F9FAFB', borderRadius:8, padding:'10px 16px', flex:'1 1 140px' }}>
                <div style={{ fontSize:'0.65rem', color:'#9CA3AF', textTransform:'uppercase', letterSpacing:'0.06em' }}>{m.label}</div>
                <div style={{ fontSize:'1.1rem', fontWeight:700, color: m.color, marginTop:2 }}>{m.prefix}{m.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── SECTION 3: Quarterly Milestones ── */}
        <SectionHead title="Quarterly Milestones" sub="FY 2026–27 · Target is equal quarterly split of annual goal" />
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
          {quarters.map((q, i) => {
            const isDone     = q.status === 'done';
            const isActive   = q.status === 'active';
            const isUpcoming = q.status === 'upcoming';
            const barColor   = isDone ? (q.achievement_pct >= 100 ? GREEN : q.achievement_pct >= 80 ? AMBER : RED) : isActive ? BRAND : '#E5E7EB';
            const bgColor    = isDone ? '#F0FDF4' : isActive ? '#EEF2FF' : '#FAFAFA';
            const statusLabel = isDone ? (q.achievement_pct >= 100 ? '✓ Achieved' : '✓ Closed') : isActive ? '● In Progress' : '○ Upcoming';
            const statusColor = isDone ? GREEN : isActive ? BRAND : '#9CA3AF';
            return (
              <div key={i} style={{ background:'#fff', border:`1px solid ${isActive ? BRAND+'40' : '#E5E7EB'}`, borderRadius:12, padding:'16px 18px', background: bgColor }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                  <div style={{ fontWeight:700, fontSize:'0.82rem', color:'#374151' }}>{q.label}</div>
                  <div style={{ fontSize:'0.68rem', fontWeight:600, color: statusColor }}>{statusLabel}</div>
                </div>
                <div style={{ fontSize:'1.4rem', fontWeight:800, color: isDone ? achieveColor(q.achievement_pct) : isActive ? BRAND : '#9CA3AF' }}>
                  {isUpcoming ? fmt(q.target) : fmt(q.actual)}
                </div>
                <div style={{ fontSize:'0.72rem', color:'#9CA3AF', marginBottom:10 }}>
                  {isUpcoming ? 'Target' : `of ${fmt(q.target)} target`}
                </div>
                {/* Progress bar */}
                <div style={{ height:6, background:'#E5E7EB', borderRadius:4, overflow:'hidden' }}>
                  <div style={{ width: `${Math.min(100, isUpcoming ? 0 : q.achievement_pct)}%`, height:'100%', background: barColor, borderRadius:4, transition:'width 0.8s ease' }} />
                </div>
                <div style={{ fontSize:'0.72rem', fontWeight:600, color: barColor, marginTop:4 }}>
                  {isUpcoming ? '—' : `${q.achievement_pct}%`}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── SECTION 4: SKU Performance ── */}
        <SectionHead title="SKU Performance — Target vs Actual vs Projected" sub="All products with FY 2026–27 targets · Click column header to sort" />
        <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
            <thead>
              <tr style={{ background:'#F9FAFB' }}>
                {[
                  { k:'name',    l:'Product / SKU' },
                  { k:'target',  l:'FY Target' },
                  { k:'actual',  l:'YTD Actual' },
                  { k:'achieve', l:'Achievement' },
                  { k:null,      l:'Pipeline' },
                  { k:null,      l:'Projected FY' },
                  { k:'growth',  l:'YoY Growth' },
                  { k:null,      l:'Progress' },
                ].map(h => (
                  <th key={h.l} onClick={() => h.k && setSkuSort(h.k)}
                    style={{ padding:'11px 14px', textAlign:'left', fontSize:'0.68rem', color:'#6B7280',
                      textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:600, whiteSpace:'nowrap',
                      cursor: h.k ? 'pointer' : 'default',
                      color: h.k && skuSort === h.k ? BRAND : '#6B7280' }}>
                    {h.l}{h.k && skuSort === h.k ? ' ↓' : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedSku.length === 0 ? (
                <tr><td colSpan={8} style={{ padding:32, textAlign:'center', color:'#9CA3AF' }}>No SKU target data</td></tr>
              ) : sortedSku.map((s, i) => {
                const achColor = achieveColor(s.achievement_pct);
                const projColor = s.projected_fy >= s.annual_target ? GREEN : AMBER;
                const growthColor = s.yoy_growth == null ? '#9CA3AF' : s.yoy_growth >= 0 ? GREEN : RED;
                return (
                  <tr key={i} style={{ borderTop:'1px solid #F3F4F6', background: i%2===0 ? '#fff' : '#FAFAFA' }}>
                    <td style={{ padding:'12px 14px', fontWeight:600, color:'#111827' }}>{s.name}</td>
                    <td style={{ padding:'12px 14px', color:'#374151' }}>{fmt(s.annual_target)}</td>
                    <td style={{ padding:'12px 14px', fontWeight:600, color: achColor }}>{fmt(s.ytd_actual)}</td>
                    <td style={{ padding:'12px 14px' }}>
                      <span style={{ background: achColor+'18', color: achColor, borderRadius:20, padding:'2px 9px', fontWeight:700, fontSize:'0.75rem' }}>
                        {s.achievement_pct}%
                      </span>
                    </td>
                    <td style={{ padding:'12px 14px', color:'#7C3AED' }}>{fmt(s.pipeline_value)}</td>
                    <td style={{ padding:'12px 14px', fontWeight:600, color: projColor }}>{fmt(s.projected_fy)}</td>
                    <td style={{ padding:'12px 14px', fontWeight:600, color: growthColor }}>
                      {s.yoy_growth != null ? pctLbl(s.yoy_growth) : <span style={{ color:'#D1D5DB' }}>—</span>}
                    </td>
                    <td style={{ padding:'12px 14px', minWidth:120 }}>
                      <div style={{ height:6, background:'#F3F4F6', borderRadius:4, overflow:'hidden' }}>
                        <div style={{ width:`${Math.min(100, s.achievement_pct)}%`, height:'100%', background: achColor, borderRadius:4 }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {sortedSku.length > 0 && (
              <tfoot>
                <tr style={{ borderTop:'2px solid #E5E7EB', background:'#F9FAFB' }}>
                  <td style={{ padding:'12px 14px', fontWeight:700, color:'#111827' }}>Total</td>
                  <td style={{ padding:'12px 14px', fontWeight:700 }}>{fmt(sortedSku.reduce((s,r)=>s+r.annual_target,0))}</td>
                  <td style={{ padding:'12px 14px', fontWeight:700, color: BRAND }}>{fmt(sortedSku.reduce((s,r)=>s+r.ytd_actual,0))}</td>
                  <td style={{ padding:'12px 14px', fontWeight:700, color: achieveColor(revenue.achievement_pct) }}>{revenue.achievement_pct}%</td>
                  <td style={{ padding:'12px 14px', fontWeight:700, color: PURPLE }}>{fmt(sortedSku.reduce((s,r)=>s+r.pipeline_value,0))}</td>
                  <td style={{ padding:'12px 14px', fontWeight:700, color: projection.on_track ? GREEN : AMBER }}>{fmt(projection.projected_fy)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* ── SECTION 5: Market reach ── */}
        <SectionHead title="Market Reach & Growth" sub="Doctor universe expansion, FOT penetration, and field activity" />
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>

          {/* Doctor KPIs */}
          <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:'18px 20px' }}>
            <div style={{ fontWeight:700, fontSize:'0.85rem', marginBottom:14 }}>🏥 Doctor Universe</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {[
                { l:'Total Doctors',    v: fmtN(doctors.total),                   c:'#111827' },
                { l:'Priority A',       v: fmtN(doctors.priority_a),              c: GREEN },
                { l:'FOT Installed',    v: fmtN(doctors.fot_count),               c: BRAND },
                { l:'FOT Penetration',  v: `${doctors.fot_penetration_pct}%`,     c: BRAND },
                { l:'New (30d)',        v: `+${fmtN(doctors.new_30d)}`,           c: AMBER },
                { l:'Rev / Doctor',    v: fmt(doctors.revenue_per_doctor),         c: PURPLE },
              ].map(m => (
                <div key={m.l} style={{ background:'#F9FAFB', borderRadius:8, padding:'10px 12px' }}>
                  <div style={{ fontSize:'0.63rem', color:'#9CA3AF', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:3 }}>{m.l}</div>
                  <div style={{ fontSize:'1.2rem', fontWeight:700, color: m.c }}>{m.v}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop:12, background:'#EEF2FF', borderRadius:8, padding:'9px 12px', fontSize:'0.75rem', color:'#3730A3' }}>
              <strong>{territories.covered}</strong> of <strong>{territories.total}</strong> territories covered ({territories.coverage_pct}%)
            </div>
          </div>

          {/* Doctor growth chart */}
          <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:'18px 16px' }}>
            <div style={{ fontWeight:700, fontSize:'0.85rem', marginBottom:12 }}>📈 Doctor Additions — Last 12M</div>
            {doctor_growth.length === 0 ? (
              <div style={{ textAlign:'center', color:'#9CA3AF', padding:32 }}>No data</div>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={doctor_growth} margin={{ left:0, right:8 }}>
                  <XAxis dataKey="month" tick={{ fontSize:10, fill:'#9CA3AF' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize:10, fill:'#9CA3AF' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="new_doctors" name="New Doctors" fill="#C7D2FE" radius={[3,3,0,0]} />
                  <Bar dataKey="new_fot"     name="New FOT"     fill={BRAND}    radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Field activity */}
          <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:'18px 16px' }}>
            <div style={{ fontWeight:700, fontSize:'0.85rem', marginBottom:12 }}>📍 Field Activity — Visits & Demos</div>
            {field_activity.length === 0 ? (
              <div style={{ textAlign:'center', color:'#9CA3AF', padding:32 }}>No data</div>
            ) : (
              <ResponsiveContainer width="100%" height={180}>
                <ComposedChart data={field_activity} margin={{ left:0, right:8 }}>
                  <XAxis dataKey="month" tick={{ fontSize:10, fill:'#9CA3AF' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize:10, fill:'#9CA3AF' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="visits" name="Visits" fill="#C7D2FE" radius={[3,3,0,0]} />
                  <Line type="monotone" dataKey="demos" name="Demos" stroke={BRAND} strokeWidth={2} dot={{ r:3, fill:BRAND }} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* ── SECTION 6: Pipeline ── */}
        <SectionHead title="Sales Pipeline" sub="Live order funnel — approved through collected" />
        <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:'20px 24px' }}>
          <div style={{ display:'flex', gap:24, flexWrap:'wrap' }}>
            {pipelineStages.map(s => {
              const d = pipeline.by_status[s.key] || { count:0, value:0 };
              const pct = totalPipelineValue > 0 ? Math.round(d.value / totalPipelineValue * 100) : 0;
              return (
                <div key={s.key} style={{ flex:'1 1 140px', borderLeft:`3px solid ${s.color}`, paddingLeft:12 }}>
                  <div style={{ fontSize:'0.68rem', color:'#9CA3AF', textTransform:'uppercase', letterSpacing:'0.05em' }}>{s.label}</div>
                  <div style={{ fontSize:'1.25rem', fontWeight:800, color:'#111827', marginTop:2 }}>{fmt(d.value)}</div>
                  <div style={{ fontSize:'0.72rem', color:'#6B7280' }}>{d.count} orders · {pct}% of total</div>
                  <div style={{ height:4, background:'#F3F4F6', borderRadius:4, marginTop:8, overflow:'hidden' }}>
                    <div style={{ width:`${pct}%`, height:'100%', background: s.color, borderRadius:4 }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop:20, paddingTop:16, borderTop:'1px solid #F3F4F6', display:'flex', justifyContent:'space-between', fontSize:'0.82rem' }}>
            <span style={{ color:'#6B7280' }}>Total pipeline value</span>
            <span style={{ fontWeight:800, color:'#111827', fontSize:'1rem' }}>{fmt(totalPipelineValue)}</span>
          </div>
        </div>

        {/* ── SECTION 7: Investor Updates (flagged) ── */}
        {portalConfig.investor_updates_enabled && updates.length > 0 && (() => {
          const unread = updates.filter(u => !u.is_read).length;
          const TYPE_META = {
            QUARTERLY_UPDATE: { label:'Quarterly Update', bg:'#EEF2FF', fg: BRAND },
            PRODUCT_UPDATE:   { label:'Product Update',   bg:'#F0FDF4', fg: GREEN },
            BOARD_NOTICE:     { label:'Board Notice',     bg:'#FEF3C7', fg: AMBER },
            FINANCIAL:        { label:'Financial Update', bg:'#FDF2F8', fg: PURPLE },
            GENERAL:          { label:'Update',           bg:'#F3F4F6', fg:'#6B7280' },
          };
          return (
            <>
              <SectionHead
                title={<span>Investor Updates {unread > 0 && <span style={{ marginLeft:8, background:'#DC2626', color:'#fff', borderRadius:99, padding:'1px 8px', fontSize:'0.65rem', fontWeight:700 }}>{unread} new</span>}</span>}
                sub="Communications from the Alveofit management team"
              />
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {updates.map(u => {
                  const tm = TYPE_META[u.type] || TYPE_META.GENERAL;
                  const isOpen = expandedUpdate === u.id;
                  const fmtPub = u.published_at ? new Date(u.published_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '';
                  return (
                    <div key={u.id} style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, overflow:'hidden', boxShadow: !u.is_read ? '0 0 0 2px '+BRAND+'30' : 'none' }}>
                      <div
                        onClick={() => { setExpandedUpdate(isOpen ? null : u.id); if (!u.is_read) markRead(u.id); }}
                        style={{ padding:'16px 20px', cursor:'pointer', display:'flex', alignItems:'center', gap:12 }}
                      >
                        {!u.is_read && <div style={{ width:8, height:8, borderRadius:'50%', background: BRAND, flexShrink:0 }} />}
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                            <span style={{ fontWeight:700, fontSize:'0.92rem', color:'#111827' }}>{u.title}</span>
                            <span style={{ fontSize:'0.67rem', fontWeight:700, padding:'2px 8px', borderRadius:99, background: tm.bg, color: tm.fg }}>{tm.label}</span>
                          </div>
                          <div style={{ fontSize:'0.75rem', color:'#9CA3AF', marginTop:3 }}>
                            {u.author_name || 'Management'} · {fmtPub}
                          </div>
                        </div>
                        <span style={{ fontSize:'0.9rem', color:'#9CA3AF', flexShrink:0 }}>{isOpen ? '▲' : '▼'}</span>
                      </div>
                      {isOpen && (
                        <div style={{ padding:'0 20px 20px', borderTop:'1px solid #F3F4F6' }}>
                          <div style={{ marginTop:14, fontSize:'0.88rem', color:'#374151', lineHeight:1.75, whiteSpace:'pre-wrap' }}>{u.body}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          );
        })()}

        {/* ── SECTION 8: Performance Commentary (flagged) ── */}
        {portalConfig.investor_performance_notes_enabled && perfNotes.length > 0 && (
          <>
            <SectionHead title="Management Commentary — Planned vs Actual" sub="Quarter-by-quarter analysis: what drove the variance and what we're doing about it" />
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              {perfNotes.map(n => {
                const isOpen = expandedNote === n.id;
                const gap    = n.target_revenue != null && n.actual_revenue != null ? n.actual_revenue - n.target_revenue : null;
                const gapPct = gap != null && n.target_revenue > 0 ? Math.round(gap / n.target_revenue * 100) : null;
                const positive = gap >= 0;
                return (
                  <div key={n.id} style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, overflow:'hidden' }}>
                    <div
                      onClick={() => setExpandedNote(isOpen ? null : n.id)}
                      style={{ padding:'16px 20px', cursor:'pointer', display:'flex', alignItems:'center', gap:16 }}
                    >
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:700, fontSize:'0.9rem', color:'#111827' }}>{n.period_label}</div>
                        <div style={{ fontSize:'0.75rem', color:'#9CA3AF', marginTop:2 }}>
                          {n.authored_by ? `Authored by ${n.author_name}` : 'Management'}
                        </div>
                      </div>
                      {gap != null && (
                        <div style={{ textAlign:'right', flexShrink:0 }}>
                          <div style={{ fontSize:'1rem', fontWeight:800, color: positive ? GREEN : RED }}>
                            {positive ? '+' : ''}{fmt(gap)}
                          </div>
                          <div style={{ fontSize:'0.7rem', color: positive ? GREEN : RED, fontWeight:600 }}>
                            {positive ? '+' : ''}{gapPct}% vs target
                          </div>
                        </div>
                      )}
                      <span style={{ fontSize:'0.9rem', color:'#9CA3AF', flexShrink:0 }}>{isOpen ? '▲' : '▼'}</span>
                    </div>
                    {isOpen && (
                      <div style={{ padding:'4px 20px 20px', borderTop:'1px solid #F3F4F6' }}>
                        {/* Variance numbers */}
                        {n.target_revenue != null && (
                          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginTop:16, marginBottom:20 }}>
                            {[
                              { l:'Target',  v: fmt(n.target_revenue), c:'#374151' },
                              { l:'Actual',  v: fmt(n.actual_revenue),  c: positive ? GREEN : RED },
                              { l:'Variance',v: `${positive?'+':''}${fmt(gap)}`, c: positive ? GREEN : RED },
                            ].map(m => (
                              <div key={m.l} style={{ background:'#F9FAFB', borderRadius:8, padding:'12px 14px' }}>
                                <div style={{ fontSize:'0.65rem', color:'#9CA3AF', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 }}>{m.l}</div>
                                <div style={{ fontSize:'1.1rem', fontWeight:700, color: m.c }}>{m.v}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        {[
                          { label:'Why the variance?',     text: n.variance_reason,   icon:'🔍' },
                          { label:'What are we doing?',    text: n.corrective_action,  icon:'⚡' },
                          { label:'Revised outlook',       text: n.revised_outlook,    icon:'🔭' },
                        ].filter(s => s.text).map(s => (
                          <div key={s.label} style={{ marginBottom:14 }}>
                            <div style={{ fontSize:'0.72rem', fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>
                              {s.icon} {s.label}
                            </div>
                            <div style={{ fontSize:'0.86rem', color:'#374151', lineHeight:1.7, whiteSpace:'pre-wrap', background:'#F9FAFB', borderRadius:8, padding:'12px 14px' }}>
                              {s.text}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ── SECTION 9: Exit Roadmap (flagged) ── */}
        {portalConfig.investor_exit_roadmap_enabled && exitRoadmap && (
          <>
            <SectionHead title="Exit Roadmap & Liquidity" sub="Current thinking on investor liquidity — updated by management" />
            <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:14, padding:'24px 28px' }}>

              {/* Exit paths */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:24 }}>
                {[
                  { label:'Primary Exit Path',   text: exitRoadmap.primary_path,   icon:'🎯', color: BRAND },
                  { label:'Secondary Path',       text: exitRoadmap.secondary_path,  icon:'↩️', color: PURPLE },
                  { label:'Buyback Policy',        text: exitRoadmap.buyback_policy,  icon:'♻️', color: AMBER },
                  { label:'Target Exit Timeline',  text: exitRoadmap.target_exit_fy,  icon:'📅', color: GREEN },
                ].filter(r => r.text).map(r => (
                  <div key={r.label} style={{ background:'#F9FAFB', borderRadius:10, padding:'16px 18px', borderLeft:`3px solid ${r.color}` }}>
                    <div style={{ fontSize:'0.68rem', color:'#9CA3AF', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>{r.icon} {r.label}</div>
                    <div style={{ fontSize:'0.87rem', color:'#374151', lineHeight:1.65 }}>{r.text}</div>
                  </div>
                ))}
              </div>

              {/* Scenarios */}
              {exitRoadmap.scenarios?.length > 0 && (
                <div style={{ marginBottom:24 }}>
                  <div style={{ fontSize:'0.7rem', fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:12 }}>
                    Return Scenarios
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:`repeat(${Math.min(exitRoadmap.scenarios.length,3)},1fr)`, gap:12 }}>
                    {exitRoadmap.scenarios.map((sc, i) => (
                      <div key={i} style={{ background: i===1?'#EEF2FF':'#F9FAFB', border:`1px solid ${i===1?BRAND+'40':'#E5E7EB'}`, borderRadius:10, padding:'14px 16px' }}>
                        <div style={{ fontSize:'0.7rem', fontWeight:700, color: i===1?BRAND:'#6B7280', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 }}>{sc.label}</div>
                        {sc.valuation_cr && <div style={{ fontSize:'1.1rem', fontWeight:800, color: i===1?BRAND:'#111827' }}>₹{sc.valuation_cr}Cr</div>}
                        {sc.investor_note && <div style={{ fontSize:'0.78rem', color:'#6B7280', marginTop:4, lineHeight:1.5 }}>{sc.investor_note}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin note */}
              {exitRoadmap.admin_note && (
                <div style={{ background:'#FEF9C3', border:'1px solid #FDE047', borderRadius:10, padding:'14px 18px' }}>
                  <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#854D0E', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>📝 Note from Management</div>
                  <div style={{ fontSize:'0.86rem', color:'#78350F', lineHeight:1.7, whiteSpace:'pre-wrap' }}>{exitRoadmap.admin_note}</div>
                  <div style={{ fontSize:'0.7rem', color:'#92400E', marginTop:8 }}>
                    Last updated: {exitRoadmap.updated_at ? new Date(exitRoadmap.updated_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* ── SECTION 10: Cap Table (flagged) ── */}
        {portalConfig.investor_cap_table_enabled && capTableData && (
          <>
            <SectionHead title="My Holdings" sub="Your equity position, current fair market value and liquidity options" />
            <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:14, padding:'24px 28px' }}>

              {/* Current valuation banner */}
              {capTableData.current_valuation && (
                <div style={{ background:'#EEF2FF', border:'1px solid #C7D2FE', borderRadius:10, padding:'14px 20px', marginBottom:22, display:'flex', gap:24, flexWrap:'wrap' }}>
                  <div>
                    <div style={{ fontSize:'0.65rem', color:'#6366F1', textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:700, marginBottom:3 }}>Current Valuation</div>
                    <div style={{ fontSize:'1.4rem', fontWeight:800, color: BRAND }}>
                      ₹{(Number(capTableData.current_valuation.post_money_valuation)/1e7).toFixed(2)} Cr
                    </div>
                  </div>
                  {capTableData.current_valuation.price_per_share && (
                    <div>
                      <div style={{ fontSize:'0.65rem', color:'#6366F1', textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:700, marginBottom:3 }}>Price per Share</div>
                      <div style={{ fontSize:'1.4rem', fontWeight:800, color: BRAND }}>₹{Number(capTableData.current_valuation.price_per_share).toFixed(2)}</div>
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize:'0.65rem', color:'#6366F1', textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:700, marginBottom:3 }}>Valuation Label</div>
                    <div style={{ fontSize:'1rem', fontWeight:700, color:'#3730A3' }}>{capTableData.current_valuation.label}</div>
                    <div style={{ fontSize:'0.72rem', color:'#6366F1' }}>{capTableData.current_valuation.snapshot_date?.slice(0,10)}</div>
                  </div>
                </div>
              )}

              {/* Holdings table */}
              {capTableData.holdings?.length > 0 ? (
                <div style={{ overflow:'auto', marginBottom:24 }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
                    <thead>
                      <tr style={{ background:'#F9FAFB', borderBottom:'1px solid #E5E7EB' }}>
                        {['Round','Instrument','Shares','Amount Invested','Price Paid','Current FMV','Gain / Loss'].map(h => (
                          <th key={h} style={{ padding:'9px 14px', textAlign:'left', fontWeight:700, color:'#374151', fontSize:'0.72rem' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {capTableData.holdings.map((h,i) => (
                        <tr key={h.id} style={{ borderBottom:'1px solid #F3F4F6', background: i%2===0?'#fff':'#FAFAFA' }}>
                          <td style={{ padding:'10px 14px', fontWeight:700, color:'#111827' }}>{h.round_name}</td>
                          <td style={{ padding:'10px 14px' }}>
                            <span style={{ fontSize:'0.65rem', fontWeight:700, padding:'2px 8px', borderRadius:99, background:'#EEF2FF', color:'#4338CA' }}>{h.instrument}</span>
                          </td>
                          <td style={{ padding:'10px 14px', fontWeight:600 }}>{Number(h.shares).toLocaleString('en-IN')}</td>
                          <td style={{ padding:'10px 14px', color:'#374151' }}>₹{(Number(h.amount_invested)/1e7).toFixed(2)} Cr</td>
                          <td style={{ padding:'10px 14px', color:'#6B7280' }}>{h.price_per_share ? `₹${Number(h.price_per_share).toFixed(2)}` : '—'}</td>
                          <td style={{ padding:'10px 14px', fontWeight:700, color: BRAND }}>{h.current_value != null ? `₹${(h.current_value/1e7).toFixed(2)} Cr` : '—'}</td>
                          <td style={{ padding:'10px 14px', fontWeight:700, color: h.gain_loss == null ? '#9CA3AF' : h.gain_loss >= 0 ? GREEN : RED }}>
                            {h.gain_loss == null ? '—' : `${h.gain_loss >= 0 ? '+' : ''}₹${(Math.abs(h.gain_loss)/1e7).toFixed(2)} Cr`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop:'2px solid #E5E7EB', background:'#F9FAFB' }}>
                        <td style={{ padding:'10px 14px', fontWeight:700, color:'#111827' }}>Total</td>
                        <td />
                        <td style={{ padding:'10px 14px', fontWeight:700 }}>
                          {capTableData.holdings.reduce((s,h)=>s+Number(h.shares||0),0).toLocaleString('en-IN')}
                        </td>
                        <td style={{ padding:'10px 14px', fontWeight:700 }}>
                          ₹{(capTableData.holdings.reduce((s,h)=>s+Number(h.amount_invested||0),0)/1e7).toFixed(2)} Cr
                        </td>
                        <td />
                        <td style={{ padding:'10px 14px', fontWeight:700, color: BRAND }}>
                          {capTableData.holdings.some(h=>h.current_value!=null)
                            ? `₹${(capTableData.holdings.reduce((s,h)=>s+Number(h.current_value||0),0)/1e7).toFixed(2)} Cr`
                            : '—'}
                        </td>
                        <td style={{ padding:'10px 14px', fontWeight:700 }}>
                          {capTableData.holdings.some(h=>h.gain_loss!=null) ? (() => {
                            const g = capTableData.holdings.reduce((s,h)=>s+Number(h.gain_loss||0),0);
                            return <span style={{ color: g>=0?GREEN:RED }}>{g>=0?'+':''}₹{(Math.abs(g)/1e7).toFixed(2)} Cr</span>;
                          })() : '—'}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign:'center', padding:32, color:'#9CA3AF' }}>Your cap table data will appear here once set up by management.</div>
              )}

              {/* Liquidation requests history */}
              {capTableData.liquidation_requests?.length > 0 && (
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:'0.78rem', fontWeight:700, color:'#374151', marginBottom:10 }}>My Liquidation Requests</div>
                  {capTableData.liquidation_requests.map(r => {
                    const sc = { PENDING:{bg:'#FEF3C7',c:'#92400E'}, UNDER_REVIEW:{bg:'#DBEAFE',c:'#1E40AF'}, APPROVED:{bg:'#DCFCE7',c:'#166534'}, REJECTED:{bg:'#FEE2E2',c:'#991B1B'}, COMPLETED:{bg:'#F3F4F6',c:'#374151'} }[r.status] || {bg:'#F3F4F6',c:'#374151'};
                    return (
                      <div key={r.id} style={{ background:'#F9FAFB', borderRadius:8, padding:'12px 16px', marginBottom:8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <div>
                          <div style={{ fontSize:'0.82rem', color:'#374151' }}>{r.reason || 'Liquidation request'}</div>
                          <div style={{ fontSize:'0.72rem', color:'#9CA3AF', marginTop:2 }}>{r.requested_at?.slice(0,10)}</div>
                          {r.admin_note && <div style={{ fontSize:'0.78rem', color:'#374151', marginTop:4 }}>Admin: {r.admin_note}</div>}
                        </div>
                        <span style={{ fontSize:'0.65rem', fontWeight:700, padding:'3px 9px', borderRadius:99, background:sc.bg, color:sc.c }}>{r.status}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Liquidation request button */}
              <button onClick={() => { setLiqModal(true); setLiqMsg(null); setLiqForm({ reason:'', amount_requested:'', shares_to_liquidate:'' }); }}
                style={{ border:'1px solid #E5E7EB', background:'#F9FAFB', color:'#374151', borderRadius:8, padding:'9px 18px', fontWeight:600, fontSize:'0.82rem', cursor:'pointer' }}>
                🔄 Request Liquidity / Exit
              </button>
            </div>
          </>
        )}

        {/* ── SECTION 11: Board Resolutions (flagged) ── */}
        {portalConfig.investor_board_resolutions_enabled && boardResolutions.length > 0 && (() => {
          const pending = boardResolutions.filter(r => r.requires_acknowledgement && !r.is_acknowledged).length;
          return (
            <>
              <SectionHead
                title={<span>Board Resolutions {pending > 0 && <span style={{ marginLeft:8, background:RED, color:'#fff', borderRadius:99, padding:'1px 8px', fontSize:'0.65rem', fontWeight:700 }}>{pending} pending</span>}</span>}
                sub="Official resolutions from the board — acknowledge your receipt"
              />
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {boardResolutions.map(r => {
                  const tc = { ORDINARY:{bg:'#EEF2FF',c:'#4338CA'}, SPECIAL:{bg:'#FEF3C7',c:'#92400E'}, CIRCULAR:{bg:'#DCFCE7',c:'#166534'} }[r.type] || {bg:'#F3F4F6',c:'#374151'};
                  const needsAck = r.requires_acknowledgement && !r.is_acknowledged;
                  return (
                    <div key={r.id} style={{ background:'#fff', border:`1px solid ${needsAck ? RED+'40' : '#E5E7EB'}`, borderRadius:12, padding:'18px 22px',
                      boxShadow: needsAck ? `0 0 0 2px ${RED}20` : 'none' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:16 }}>
                        <div style={{ flex:1 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:8 }}>
                            <span style={{ fontWeight:800, fontSize:'0.95rem', color:'#111827' }}>{r.title}</span>
                            <span style={{ fontSize:'0.65rem', fontWeight:700, padding:'2px 8px', borderRadius:99, background:tc.bg, color:tc.c }}>{r.type}</span>
                            {r.requires_acknowledgement && (
                              r.is_acknowledged
                                ? <span style={{ fontSize:'0.65rem', fontWeight:700, padding:'2px 8px', borderRadius:99, background:'#DCFCE7', color:'#166534' }}>✓ Acknowledged</span>
                                : <span style={{ fontSize:'0.65rem', fontWeight:700, padding:'2px 8px', borderRadius:99, background:'#FEE2E2', color:RED }}>⚠ Needs Acknowledgement</span>
                            )}
                          </div>
                          <div style={{ fontSize:'0.84rem', color:'#374151', lineHeight:1.7, marginBottom:10, whiteSpace:'pre-wrap' }}>{r.body}</div>
                          <div style={{ fontSize:'0.72rem', color:'#9CA3AF' }}>
                            {r.resolution_date?.slice(0,10)} · {r.author_name || 'Board'}
                            {r.acknowledged_at && ` · Acknowledged ${r.acknowledged_at?.slice(0,10)}`}
                          </div>
                        </div>
                        {needsAck && (
                          <button
                            onClick={async () => {
                              await api.post(`/investor-portal/board-resolutions/${r.id}/acknowledge`).catch(() => {});
                              setBoardResolutions(prev => prev.map(res => res.id === r.id ? { ...res, is_acknowledged: true, acknowledged_at: new Date().toISOString() } : res));
                            }}
                            style={{ background: BRAND, color:'#fff', border:'none', borderRadius:8, padding:'9px 18px', fontWeight:700, fontSize:'0.82rem', cursor:'pointer', flexShrink:0 }}>
                            ✓ Acknowledge
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          );
        })()}

        {/* Footer */}
        <div style={{ marginTop:48, textAlign:'center', fontSize:'0.68rem', color:'#D1D5DB', lineHeight:1.8 }}>
          CONFIDENTIAL — Authorised investor use only. Live operational data. Do not distribute.<br/>
          Alveofit · Roundworks Technologies Pvt Ltd · Pune
        </div>
      </div>

      {/* ── Liquidation Request Modal ── */}
      {liqModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => { if(e.target===e.currentTarget) setLiqModal(false); }}>
          <div style={{ background:'#fff', borderRadius:14, padding:'28px 32px', width:460, boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <div style={{ fontWeight:800, fontSize:'1rem', color:'#111827' }}>🔄 Request Liquidity / Exit</div>
              <button onClick={() => setLiqModal(false)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:'1.2rem', color:'#9CA3AF' }}>✕</button>
            </div>
            <div style={{ fontSize:'0.82rem', color:'#6B7280', marginBottom:18, lineHeight:1.6 }}>
              Submit a liquidity or exit request to the management team. They will review and respond within 5 business days.
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:'0.78rem', fontWeight:600, color:'#374151', display:'block', marginBottom:4 }}>Reason *</label>
              <textarea value={liqForm.reason} onChange={e => setLiqForm(f=>({...f,reason:e.target.value}))} required
                rows={3} placeholder="Describe why you are requesting liquidity…"
                style={{ width:'100%', padding:'9px 12px', border:'1px solid #E5E7EB', borderRadius:8, fontSize:'0.85rem', boxSizing:'border-box', resize:'vertical' }} />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:18 }}>
              <div>
                <label style={{ fontSize:'0.78rem', fontWeight:600, color:'#374151', display:'block', marginBottom:4 }}>Amount Requested (₹)</label>
                <input type="number" value={liqForm.amount_requested} onChange={e => setLiqForm(f=>({...f,amount_requested:e.target.value}))}
                  placeholder="Optional"
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid #E5E7EB', borderRadius:8, fontSize:'0.85rem', boxSizing:'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize:'0.78rem', fontWeight:600, color:'#374151', display:'block', marginBottom:4 }}>Shares to Liquidate</label>
                <input type="number" value={liqForm.shares_to_liquidate} onChange={e => setLiqForm(f=>({...f,shares_to_liquidate:e.target.value}))}
                  placeholder="Optional"
                  style={{ width:'100%', padding:'9px 12px', border:'1px solid #E5E7EB', borderRadius:8, fontSize:'0.85rem', boxSizing:'border-box' }} />
              </div>
            </div>
            {liqMsg && (
              <div style={{ padding:'9px 12px', borderRadius:8, marginBottom:12, fontSize:'0.8rem', fontWeight:500,
                background: liqMsg.ok ? '#D1FAE5' : '#FEE2E2', color: liqMsg.ok ? '#065F46' : '#991B1B' }}>
                {liqMsg.ok ? '✓' : '⚠'} {liqMsg.text}
              </div>
            )}
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => setLiqModal(false)} style={{ background:'#F3F4F6', color:'#374151', border:'none', borderRadius:8, padding:'9px 18px', fontWeight:700, fontSize:'0.82rem', cursor:'pointer' }}>
                Cancel
              </button>
              <button onClick={async () => {
                if (!liqForm.reason?.trim()) { setLiqMsg({ ok:false, text:'Please provide a reason.' }); return; }
                setLiqSubmitting(true); setLiqMsg(null);
                try {
                  await api.post('/investor-portal/liquidation-request', liqForm);
                  setLiqMsg({ ok:true, text:'Request submitted. Management will review within 5 business days.' });
                  setCapTableData(d => d ? { ...d, liquidation_requests: [...(d.liquidation_requests||[]), { id: Date.now(), ...liqForm, status:'PENDING', requested_at: new Date().toISOString() }] } : d);
                  setTimeout(() => setLiqModal(false), 2000);
                } catch(e) { setLiqMsg({ ok:false, text: e.response?.data?.message || 'Failed to submit.' }); }
                finally { setLiqSubmitting(false); }
              }} disabled={liqSubmitting}
                style={{ background: BRAND, color:'#fff', border:'none', borderRadius:8, padding:'9px 18px', fontWeight:700, fontSize:'0.82rem', cursor:'pointer', opacity:liqSubmitting?0.7:1 }}>
                {liqSubmitting ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Change Password Modal ── */}
      {showPwdModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => { if(e.target===e.currentTarget) setShowPwdModal(false); }}>
          <div style={{ background:'#fff', borderRadius:14, padding:'28px 32px', width:380, boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <div style={{ fontWeight:800, fontSize:'1rem', color:'#111827' }}>🔑 Change Password</div>
              <button onClick={() => setShowPwdModal(false)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:'1.2rem', color:'#9CA3AF' }}>✕</button>
            </div>
            <form onSubmit={handleChangePwd}>
              {['current','next','confirm'].map((f,i) => (
                <div key={f} style={{ marginBottom:14 }}>
                  <label style={{ fontSize:'0.78rem', fontWeight:600, color:'#374151', display:'block', marginBottom:4 }}>
                    {f==='current' ? 'Current Password' : f==='next' ? 'New Password' : 'Confirm New Password'}
                  </label>
                  <input type="password" value={pwdForm[f]} onChange={e => setPwdForm(p=>({...p,[f]:e.target.value}))}
                    placeholder="••••••••" required
                    style={{ width:'100%', padding:'9px 12px', border:'1px solid #E5E7EB', borderRadius:8, fontSize:'0.88rem', outline:'none', boxSizing:'border-box' }} />
                </div>
              ))}
              {pwdMsg && (
                <div style={{ padding:'9px 12px', borderRadius:8, marginBottom:12, fontSize:'0.8rem', fontWeight:500,
                  background: pwdMsg.ok ? '#D1FAE5' : '#FEE2E2', color: pwdMsg.ok ? '#065F46' : '#991B1B' }}>
                  {pwdMsg.ok ? '✓' : '⚠'} {pwdMsg.text}
                </div>
              )}
              <button type="submit" disabled={pwdLoading}
                style={{ width:'100%', padding:'10px', background:BRAND, color:'#fff', border:'none', borderRadius:8, fontSize:'0.88rem', fontWeight:700, cursor:pwdLoading?'not-allowed':'pointer', opacity:pwdLoading?0.7:1 }}>
                {pwdLoading ? 'Updating…' : 'Update Password'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
